'use client';

import { useState, useEffect, useCallback } from 'react';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ContactPerson {
  name: string;
  phone?: string;
  email?: string;
}

interface ContactDirectory {
  homeowner: ContactPerson;
  project_manager: ContactPerson;
  foreman: ContactPerson;
  qc_inspector: ContactPerson;
}

interface WarrantyRegistration {
  id: string;
  job_id: string;
  customer_last_name: string;
  install_date: string;
  material_brand: string;
  material_type: string;
  status: string;
  filed_date: string | null;
  contact_directory: ContactDirectory;
  created_at: string;
}

interface ActionLogEntry {
  date: string;
  actor: string;
  action: string;
}

interface PhotoEntry {
  url: string;
  alt: string;
  caption: string;
}

interface WarrantyClaim {
  id: string;
  job_id: string;
  customer_name: string;
  install_date: string;
  reported_issue: string;
  status: string;
  complaint_text: string;
  action_log: ActionLogEntry[];
  qc_photos: PhotoEntry[];
  claim_photos: PhotoEntry[];
  contact_directory: ContactDirectory;
  created_at: string;
}

type DrawerItem =
  | { type: 'registration'; data: WarrantyRegistration }
  | { type: 'claim'; data: WarrantyClaim };

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = (dateStr: string) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const statusLabel: Record<string, string> = {
  investigation_pending: 'Investigation Pending',
  crew_dispatched: 'Crew Dispatched',
  resolved: 'Resolved',
  pending: 'Pending Filing',
  filed: 'Filed',
};

const statusColor: Record<string, string> = {
  investigation_pending: 'bg-red-100 text-red-700 border border-red-200',
  crew_dispatched: 'bg-orange-100 text-orange-700 border border-orange-200',
  resolved: 'bg-green-100 text-green-700 border border-green-200',
  pending: 'bg-yellow-100 text-yellow-700 border border-yellow-200',
  filed: 'bg-green-100 text-green-700 border border-green-200',
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function MetricCard({ icon, label, value, color }: { icon: string; label: string; value: string | number; color: string }) {
  return (
    <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 flex items-center gap-4">
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
        <Icon name={icon as any} size={22} variant="solid" className="text-white" />
      </div>
      <div>
        <p className="font-body text-xs text-gray-500 mb-0.5">{label}</p>
        <p className="font-heading text-2xl font-bold" style={{ color: '#1C2E5E' }}>{value}</p>
      </div>
    </div>
  );
}

function RegistrationCard({
  item,
  onClick,
  onMarkFiled,
}: {
  item: WarrantyRegistration;
  onClick: () => void;
  onMarkFiled: (id: string) => void;
}) {
  return (
    <div
      className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md hover:border-cyan-200 transition-all duration-200 cursor-pointer"
      onClick={onClick}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <p className="font-heading font-semibold text-base" style={{ color: '#1C2E5E' }}>
            {item.customer_last_name} Family
          </p>
          <p className="font-body text-xs text-gray-500 mt-0.5">
            {item.job_id} · Installed {formatDate(item.install_date)}
          </p>
        </div>
        <span className={`flex-shrink-0 text-xs font-medium px-2.5 py-1 rounded-full ${statusColor[item.status] || statusColor.pending}`}>
          {statusLabel[item.status] || 'Pending Filing'}
        </span>
      </div>

      {/* Material badge */}
      <div className="mb-3">
        <span
          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg"
          style={{ background: 'rgba(3,52,148,0.08)', color: '#1C2E5E' }}
        >
          <Icon name="ShieldCheckIcon" size={13} variant="solid" />
          {item.material_brand} · {item.material_type}
        </span>
      </div>

      <div className="flex items-center justify-between">
        <p className="font-body text-xs text-gray-400">
          {item.contact_directory?.homeowner?.name || '—'}
        </p>
        <button
          onClick={(e) => { e.stopPropagation(); onMarkFiled(item.id); }}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white transition-all duration-200 hover:opacity-90 active:scale-95"
          style={{ background: '#E89A3C' }}
        >
          <Icon name="CheckCircleIcon" size={13} variant="solid" />
          Mark as Filed
        </button>
      </div>
    </div>
  );
}

function ClaimCard({ item, onClick }: { item: WarrantyClaim; onClick: () => void }) {
  return (
    <div
      className="bg-white rounded-xl p-4 shadow-sm border border-red-100 hover:shadow-md hover:border-red-300 transition-all duration-200 cursor-pointer"
      onClick={onClick}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <p className="font-heading font-semibold text-base" style={{ color: '#1C2E5E' }}>
            {item.customer_name}
          </p>
          <p className="font-body text-xs text-gray-500 mt-0.5">
            {item.job_id} · Installed {formatDate(item.install_date)}
          </p>
        </div>
        <span className={`flex-shrink-0 text-xs font-medium px-2.5 py-1 rounded-full ${statusColor[item.status] || statusColor.investigation_pending}`}>
          {statusLabel[item.status] || 'Investigation Pending'}
        </span>
      </div>

      <div className="flex items-start gap-2 mb-3">
        <Icon name="ExclamationTriangleIcon" size={14} variant="solid" className="text-red-500 flex-shrink-0 mt-0.5" />
        <p className="font-body text-sm text-gray-700 leading-snug line-clamp-2">{item.reported_issue}</p>
      </div>

      <div className="flex items-center justify-between">
        <p className="font-body text-xs text-gray-400">
          {item.action_log?.length || 0} log entries
        </p>
        <button
          onClick={(e) => { e.stopPropagation(); }}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white transition-all duration-200 hover:opacity-90 active:scale-95"
          style={{ background: '#EF4444' }}
        >
          <Icon name="EyeIcon" size={13} variant="solid" />
          View Claim
        </button>
      </div>
    </div>
  );
}

// ─── Slide-out Drawer ─────────────────────────────────────────────────────────

function ClaimDrawer({ item, onClose }: { item: DrawerItem; onClose: () => void }) {
  const isRegistration = item.type === 'registration';
  const data = item.data;
  const contacts = data.contact_directory;

  const jobId = data.job_id;
  const customerName = isRegistration
    ? `${(data as WarrantyRegistration).customer_last_name} Family`
    : (data as WarrantyClaim).customer_name;
  const material = isRegistration
    ? `${(data as WarrantyRegistration).material_brand} · ${(data as WarrantyRegistration).material_type}`
    : '';

  const claimData = isRegistration ? null : (data as WarrantyClaim);

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 z-40 transition-opacity duration-300"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div className="fixed top-0 right-0 h-full w-full max-w-2xl bg-white z-50 shadow-2xl flex flex-col overflow-hidden animate-slide-in-right">
        {/* Header */}
        <div className="flex-shrink-0 px-6 py-5 border-b border-gray-100" style={{ background: '#1C2E5E' }}>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-caption text-xs text-white/60 bg-white/10 px-2 py-0.5 rounded-md">{jobId}</span>
                {!isRegistration && claimData && (
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusColor[claimData.status] || statusColor.investigation_pending}`}>
                    {statusLabel[claimData.status]}
                  </span>
                )}
              </div>
              <h2 className="font-heading text-xl font-bold text-white truncate">{customerName}</h2>
              {material && (
                <p className="font-body text-sm text-white/60 mt-0.5">{material}</p>
              )}
              {!isRegistration && claimData && (
                <p className="font-body text-sm text-white/70 mt-1 line-clamp-2">{claimData.reported_issue}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="flex-shrink-0 p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-standard"
              aria-label="Close drawer"
            >
              <Icon name="XMarkIcon" size={20} />
            </button>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-3 mt-4">
            <button
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border-2 border-white/30 text-white hover:bg-white/10 transition-standard"
            >
              <Icon name="MagnifyingGlassIcon" size={15} variant="outline" />
              Dispatch Inspector
            </button>
            <button
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-standard hover:opacity-90"
              style={{ background: '#EF4444' }}
            >
              <Icon name="DocumentTextIcon" size={15} variant="solid" />
              Submit Manufacturer Claim
            </button>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6" style={{ background: '#F8F9FA' }}>

          {/* 2x2 Contact Grid */}
          <section>
            <h3 className="font-heading text-sm font-semibold mb-3" style={{ color: '#1C2E5E' }}>
              Contact Directory
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {/* Homeowner */}
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'rgba(232,154,60,0.12)' }}>
                    <Icon name="HomeIcon" size={14} variant="solid" style={{ color: '#E89A3C' }} />
                  </div>
                  <span className="font-caption text-xs font-semibold text-gray-500 uppercase tracking-wide">Homeowner</span>
                </div>
                <p className="font-body text-sm font-semibold" style={{ color: '#1C2E5E' }}>{contacts?.homeowner?.name || '—'}</p>
                {contacts?.homeowner?.phone && (
                  <a href={`tel:${contacts.homeowner.phone}`} className="flex items-center gap-1.5 mt-1.5 text-xs text-gray-500 hover:text-cyan-600 transition-standard">
                    <Icon name="PhoneIcon" size={11} variant="outline" />
                    {contacts.homeowner.phone}
                  </a>
                )}
                {contacts?.homeowner?.email && (
                  <a href={`mailto:${contacts.homeowner.email}`} className="flex items-center gap-1.5 mt-1 text-xs text-gray-500 hover:text-cyan-600 transition-standard truncate">
                    <Icon name="EnvelopeIcon" size={11} variant="outline" />
                    {contacts.homeowner.email}
                  </a>
                )}
              </div>

              {/* Project Manager */}
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'rgba(3,52,148,0.1)' }}>
                    <Icon name="BriefcaseIcon" size={14} variant="solid" style={{ color: '#1C2E5E' }} />
                  </div>
                  <span className="font-caption text-xs font-semibold text-gray-500 uppercase tracking-wide">Project Manager</span>
                </div>
                <p className="font-body text-sm font-semibold" style={{ color: '#1C2E5E' }}>{contacts?.project_manager?.name || '—'}</p>
                {contacts?.project_manager?.phone && (
                  <p className="flex items-center gap-1.5 mt-1.5 text-xs text-gray-500">
                    <Icon name="PhoneIcon" size={11} variant="outline" />
                    {contacts.project_manager.phone}
                  </p>
                )}
                <button className="flex items-center gap-1.5 mt-2 text-xs font-semibold hover:opacity-80 transition-standard" style={{ color: '#E89A3C' }}>
                  <Icon name="ChatBubbleLeftRightIcon" size={12} variant="outline" />
                  Quick Message
                </button>
              </div>

              {/* Original Crew Foreman */}
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'rgba(16,185,129,0.1)' }}>
                    <Icon name="WrenchScrewdriverIcon" size={14} variant="solid" className="text-green-600" />
                  </div>
                  <span className="font-caption text-xs font-semibold text-gray-500 uppercase tracking-wide">Crew Foreman</span>
                </div>
                <p className="font-body text-sm font-semibold" style={{ color: '#1C2E5E' }}>{contacts?.foreman?.name || '—'}</p>
                {contacts?.foreman?.phone && (
                  <a href={`tel:${contacts.foreman.phone}`} className="flex items-center gap-1.5 mt-1.5 text-xs text-gray-500 hover:text-cyan-600 transition-standard">
                    <Icon name="PhoneIcon" size={11} variant="outline" />
                    {contacts.foreman.phone}
                  </a>
                )}
              </div>

              {/* QC Inspector */}
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'rgba(245,158,11,0.1)' }}>
                    <Icon name="ClipboardDocumentCheckIcon" size={14} variant="solid" className="text-yellow-600" />
                  </div>
                  <span className="font-caption text-xs font-semibold text-gray-500 uppercase tracking-wide">QC Inspector</span>
                </div>
                <p className="font-body text-sm font-semibold" style={{ color: '#1C2E5E' }}>{contacts?.qc_inspector?.name || '—'}</p>
                {contacts?.qc_inspector?.phone && (
                  <p className="flex items-center gap-1.5 mt-1.5 text-xs text-gray-500">
                    <Icon name="PhoneIcon" size={11} variant="outline" />
                    {contacts.qc_inspector.phone}
                  </p>
                )}
                <button className="flex items-center gap-1.5 mt-2 text-xs font-semibold hover:opacity-80 transition-standard" style={{ color: '#E89A3C' }}>
                  <Icon name="ChatBubbleLeftRightIcon" size={12} variant="outline" />
                  Quick Message
                </button>
              </div>
            </div>
          </section>

          {/* Claim Details & Log (claims only) */}
          {claimData && (
            <section>
              <h3 className="font-heading text-sm font-semibold mb-3" style={{ color: '#1C2E5E' }}>
                Claim Details & Action Log
              </h3>
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 mb-3">
                <p className="font-caption text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Customer Complaint</p>
                <p className="font-body text-sm text-gray-700 leading-relaxed">{claimData.complaint_text}</p>
              </div>

              {/* Timeline */}
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <p className="font-caption text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Action Timeline</p>
                <div className="space-y-4">
                  {(claimData.action_log || []).map((entry, idx) => (
                    <div key={idx} className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <div className="w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1" style={{ background: '#E89A3C' }} />
                        {idx < (claimData.action_log?.length || 0) - 1 && (
                          <div className="w-px flex-1 mt-1" style={{ background: '#E5E7EB' }} />
                        )}
                      </div>
                      <div className="pb-3 flex-1">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="font-body text-xs font-semibold text-gray-800">{entry.actor}</span>
                          <span className="font-caption text-xs text-gray-400">{formatDate(entry.date)}</span>
                        </div>
                        <p className="font-body text-sm text-gray-600 leading-snug">{entry.action}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* Photo Evidence Viewer */}
          {claimData && (
            <section>
              <h3 className="font-heading text-sm font-semibold mb-3" style={{ color: '#1C2E5E' }}>
                Photo Evidence
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {/* QC Photos */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-green-500" />
                    <p className="font-caption text-xs font-semibold text-green-700 uppercase tracking-wide">Original QC Completion Photos</p>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {(claimData.qc_photos || []).map((photo, idx) => (
                      <div key={idx} className="relative group rounded-lg overflow-hidden bg-gray-100 aspect-square border-2 border-green-100">
                        <img
                          src={photo.url}
                          alt={photo.alt}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-200 flex items-end">
                          <p className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-white text-[10px] font-caption p-1.5 leading-tight">
                            {photo.caption}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Claim Photos */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-red-500" />
                    <p className="font-caption text-xs font-semibold text-red-700 uppercase tracking-wide">Customer Submitted Claim Photos</p>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {(claimData.claim_photos || []).map((photo, idx) => (
                      <div key={idx} className="relative group rounded-lg overflow-hidden bg-gray-100 aspect-square border-2 border-red-100">
                        <img
                          src={photo.url}
                          alt={photo.alt}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-200 flex items-end">
                          <p className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-white text-[10px] font-caption p-1.5 leading-tight">
                            {photo.caption}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Registration info (for registration drawer) */}
          {isRegistration && (
            <section>
              <h3 className="font-heading text-sm font-semibold mb-3" style={{ color: '#1C2E5E' }}>
                Registration Details
              </h3>
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="font-body text-sm text-gray-500">Job ID</span>
                  <span className="font-body text-sm font-semibold" style={{ color: '#1C2E5E' }}>{data.job_id}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-body text-sm text-gray-500">Install Date</span>
                  <span className="font-body text-sm font-semibold" style={{ color: '#1C2E5E' }}>{formatDate((data as WarrantyRegistration).install_date)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-body text-sm text-gray-500">Material Brand</span>
                  <span className="font-body text-sm font-semibold" style={{ color: '#1C2E5E' }}>{(data as WarrantyRegistration).material_brand}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-body text-sm text-gray-500">Material Type</span>
                  <span className="font-body text-sm font-semibold text-gray-700">{(data as WarrantyRegistration).material_type}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-body text-sm text-gray-500">Status</span>
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${statusColor[(data as WarrantyRegistration).status] || statusColor.pending}`}>
                    {statusLabel[(data as WarrantyRegistration).status] || 'Pending Filing'}
                  </span>
                </div>
              </div>
            </section>
          )}
        </div>
      </div>
    </>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function WarrantyClaimsInteractive() {
  const { currentUser } = useAuth();
  const supabase = createClient();

  const [registrations, setRegistrations] = useState<WarrantyRegistration[]>([]);
  const [claims, setClaims] = useState<WarrantyClaim[]>([]);
  const [loading, setLoading] = useState(true);
  const [drawerItem, setDrawerItem] = useState<DrawerItem | null>(null);
  const [isClockedIn, setIsClockedIn] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [warrantyEscalations, setWarrantyEscalations] = useState<any[]>([]);
  const [loadingWarrantyEscalations, setLoadingWarrantyEscalations] = useState(false);

  const userInitials = `${currentUser.first_name.charAt(0)}${currentUser.last_name.charAt(0)}`;

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchData = useCallback(async () => {
    try {
      const [regRes, claimRes] = await Promise.all([
        supabase.from('warranty_registrations').select('*').order('created_at', { ascending: false }),
        supabase.from('warranty_claims').select('*').order('created_at', { ascending: false }),
      ]);
      if (regRes.data) setRegistrations(regRes.data as WarrantyRegistration[]);
      if (claimRes.data) setClaims(claimRes.data as WarrantyClaim[]);
    } catch {
      // silently handle
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  const fetchWarrantyEscalations = useCallback(async () => {
    setLoadingWarrantyEscalations(true);
    try {
      const { data } = await supabase
        .from('escalations')
        .select('id, ticket_id, title, priority, status, affected_job_id, created_at, response_due, resolution_due')
        .eq('source_department', 'Warranty')
        .neq('status', 'closed')
        .order('created_at', { ascending: false })
        .limit(10);
      setWarrantyEscalations(data || []);
    } catch {
      setWarrantyEscalations([]);
    } finally {
      setLoadingWarrantyEscalations(false);
    }
  }, [supabase]);

  useEffect(() => {
    fetchData();
    fetchWarrantyEscalations();

    // Real-time subscriptions
    const regChannel = supabase
      .channel('warranty_registrations_rt')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'warranty_registrations' }, fetchData)
      .subscribe();

    const claimChannel = supabase
      .channel('warranty_claims_rt')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'warranty_claims' }, fetchData)
      .subscribe();

    return () => {
      supabase.removeChannel(regChannel);
      supabase.removeChannel(claimChannel);
    };
  }, [fetchData, supabase]);

  const handleMarkFiled = async (id: string) => {
    const { error } = await supabase
      .from('warranty_registrations')
      .update({ status: 'filed', filed_date: new Date().toISOString().split('T')[0] })
      .eq('id', id);

    if (error) {
      showToast('Failed to update registration.', 'error');
    } else {
      showToast('Warranty marked as filed!', 'success');
      fetchData();
    }
  };

  // Metrics
  const pendingCount = registrations.filter(r => r.status === 'pending').length;
  const activeClaimsCount = claims.filter(c => c.status !== 'resolved').length;

  return (
    <div className="min-h-screen" style={{ background: '#F8F9FA' }}>

      {/* ── Sticky Page Header ─────────────────────────────────────────── */}
      <div
        className="sticky top-[60px] z-30 bg-white border-b border-gray-100 shadow-sm"
      >
        <div className="flex items-center justify-between px-6 py-3.5">
          {/* Left: Title */}
          <div>
            <h1 className="font-heading text-lg font-bold" style={{ color: '#1C2E5E' }}>
              Warranty &amp; Claims Management
            </h1>
            <p className="font-body text-xs text-gray-500 mt-0.5">
              Manufacturer registrations &amp; active customer claims
            </p>
          </div>

          {/* Right: Controls */}
          <div className="flex items-center gap-2">
            {/* Clock In/Out pill */}
            <button
              onClick={() => setIsClockedIn(p => !p)}
              className="flex items-center gap-2 px-4 h-9 rounded-full text-sm font-semibold text-white transition-all duration-200 hover:opacity-90 active:scale-95 min-w-[120px] justify-center"
              style={{ background: isClockedIn ? '#10B981' : '#E89A3C' }}
              suppressHydrationWarning
            >
              <Icon name={isClockedIn ? 'StopCircleIcon' : 'PlayCircleIcon'} size={15} variant="solid" />
              {isClockedIn ? 'Clock Out' : 'Clock In'}
            </button>

            {/* Messenger */}
            <button className="relative p-2 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition-standard min-w-[40px] min-h-[40px] flex items-center justify-center" aria-label="Messenger" suppressHydrationWarning>
              <Icon name="ChatBubbleLeftRightIcon" size={20} variant="outline" />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full" style={{ background: '#E89A3C' }} />
            </button>

            {/* Notification bell */}
            <button className="relative p-2 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition-standard min-w-[40px] min-h-[40px] flex items-center justify-center" aria-label="Notifications" suppressHydrationWarning>
              <Icon name="BellIcon" size={20} variant="outline" />
              <span className="absolute top-1 right-1 w-4 h-4 rounded-full text-white text-[10px] font-caption flex items-center justify-center" style={{ background: '#EF4444' }}>3</span>
            </button>

            {/* Avatar */}
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold cursor-pointer"
              style={{ background: 'linear-gradient(135deg, #E89A3C 0%, #1C2E5E 100%)' }}
              suppressHydrationWarning
            >
              {userInitials}
            </div>
          </div>
        </div>
      </div>

      {/* ── Main Content ───────────────────────────────────────────────── */}
      <div className="p-6 space-y-6">

        {/* Metric Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <MetricCard
            icon="ClipboardDocumentListIcon"
            label="Pending Manufacturer Registrations"
            value={loading ? '—' : pendingCount}
            color="bg-yellow-400"
          />
          <MetricCard
            icon="ExclamationTriangleIcon"
            label="Active Warranty Claims"
            value={loading ? '—' : activeClaimsCount}
            color="bg-red-500"
          />
          <MetricCard
            icon="ClockIcon"
            label="Avg. Claim Resolution Time"
            value="—"
            color="bg-cyan-500"
          />
        </div>

        {/* Two-column workspace */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* ── Left: Warranty Registration Queue ── */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-1 h-5 rounded-full" style={{ background: '#F59E0B' }} />
                <h2 className="font-heading text-base font-bold" style={{ color: '#1C2E5E' }}>
                  Warranty Registration Queue
                </h2>
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700">
                  {loading ? '—' : pendingCount} pending
                </span>
              </div>
            </div>

            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 animate-pulse">
                    <div className="h-4 bg-gray-200 rounded w-1/2 mb-2" />
                    <div className="h-3 bg-gray-100 rounded w-3/4 mb-3" />
                    <div className="h-7 bg-gray-100 rounded w-1/3" />
                  </div>
                ))}
              </div>
            ) : registrations.filter(r => r.status === 'pending').length === 0 ? (
              <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100 text-center">
                <Icon name="CheckCircleIcon" size={32} variant="solid" className="text-green-400 mx-auto mb-2" />
                <p className="font-body text-sm text-gray-500">All warranties have been filed!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {registrations.filter(r => r.status === 'pending').map(reg => (
                  <RegistrationCard
                    key={reg.id}
                    item={reg}
                    onClick={() => setDrawerItem({ type: 'registration', data: reg })}
                    onMarkFiled={handleMarkFiled}
                  />
                ))}
              </div>
            )}
          </div>

          {/* ── Right: Active Customer Claims ── */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-1 h-5 rounded-full bg-red-500" />
                <h2 className="font-heading text-base font-bold" style={{ color: '#1C2E5E' }}>
                  Active Customer Claims
                </h2>
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                  {loading ? '—' : activeClaimsCount} active
                </span>
              </div>
            </div>

            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="bg-white rounded-xl p-4 shadow-sm border border-red-100 animate-pulse">
                    <div className="h-4 bg-gray-200 rounded w-1/2 mb-2" />
                    <div className="h-3 bg-gray-100 rounded w-3/4 mb-3" />
                    <div className="h-7 bg-gray-100 rounded w-1/3" />
                  </div>
                ))}
              </div>
            ) : claims.filter(c => c.status !== 'resolved').length === 0 ? (
              <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100 text-center">
                <Icon name="ShieldCheckIcon" size={32} variant="solid" className="text-green-400 mx-auto mb-2" />
                <p className="font-body text-sm text-gray-500">No active claims at this time.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {claims.filter(c => c.status !== 'resolved').map(claim => (
                  <ClaimCard
                    key={claim.id}
                    item={claim}
                    onClick={() => setDrawerItem({ type: 'claim', data: claim })}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ─── Warranty Escalations ─────────────────────────────────────────── */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: '#FEF2F2' }}
              >
                <Icon name="ExclamationTriangleIcon" size={16} style={{ color: '#EF4444' }} />
              </div>
              <div>
                <h2 className="text-base font-bold" style={{ color: '#1C2E5E' }}>Warranty Escalations</h2>
                <p className="text-xs" style={{ color: '#6B7280' }}>Active tickets routed to Warranty</p>
              </div>
              <span
                className="ml-2 px-2 py-0.5 rounded-full text-xs font-bold"
                style={{ backgroundColor: '#FEF2F2', color: '#EF4444', border: '1px solid #FECACA' }}
              >
                {warrantyEscalations.filter(e => e.status !== 'resolved').length} OPEN
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={fetchWarrantyEscalations}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all"
              >
                <Icon name="ArrowPathIcon" size={13} />
                Refresh
              </button>
              <a
                href="/escalation-management-dashboard"
                className="text-xs font-medium flex items-center gap-1 hover:underline"
                style={{ color: '#E89A3C' }}
              >
                View All Escalations
                <Icon name="ArrowRightIcon" size={13} />
              </a>
            </div>
          </div>

          <div
            className="rounded-xl overflow-hidden"
            style={{ backgroundColor: '#FFFFFF', borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid #F0F0F0' }}
          >
            {loadingWarrantyEscalations ? (
              <div className="p-8 flex items-center justify-center gap-3">
                <div className="w-5 h-5 border-2 border-gray-200 border-t-cyan-500 rounded-full animate-spin" />
                <span className="text-sm text-gray-400">Loading escalations…</span>
              </div>
            ) : warrantyEscalations.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <div className="w-12 h-12 rounded-full flex items-center justify-center mb-3" style={{ background: '#DCFCE7' }}>
                  <Icon name="CheckCircleIcon" size={24} style={{ color: '#10B981' }} />
                </div>
                <p className="text-sm font-semibold text-gray-600">No active Warranty escalations</p>
                <p className="text-xs text-gray-400 mt-1">All Warranty tickets are resolved or closed</p>
              </div>
            ) : (
              <div className="divide-y" style={{ borderColor: '#F3F4F6' }}>
                {warrantyEscalations.map(ticket => {
                  const isHigh = ticket.priority === 'high' || ticket.priority === 'critical';
                  const isMedium = ticket.priority === 'medium';
                  const severityColor = isHigh ? '#EF4444' : isMedium ? '#F59E0B' : '#10B981';
                  const severityBg = isHigh ? '#FEF2F2' : isMedium ? '#FFFBEB' : '#F0FDF4';
                  const priorityBorderColor = isHigh ? '#FECACA' : isMedium ? '#FDE68A' : '#BBF7D0';
                  const priorityTextColor = isHigh ? '#EF4444' : isMedium ? '#D97706' : '#16A34A';
                  const statusColors: Record<string, { bg: string; text: string; border: string }> = {
                    open: { bg: '#EFF6FF', text: '#2563EB', border: '#BFDBFE' },
                    in_progress: { bg: '#FFFBEB', text: '#D97706', border: '#FDE68A' },
                    resolved: { bg: '#F0FDF4', text: '#16A34A', border: '#BBF7D0' },
                  };
                  const sc = statusColors[ticket.status] || { bg: '#F3F4F6', text: '#6B7280', border: '#E5E7EB' };
                  const isResponseBreached = ticket.response_due ? new Date(ticket.response_due).getTime() < Date.now() : false;
                  const isResolutionBreached = ticket.resolution_due ? new Date(ticket.resolution_due).getTime() < Date.now() : false;
                  const timeAgoStr = (() => {
                    const diff = Date.now() - new Date(ticket.created_at).getTime();
                    const mins = Math.floor(diff / 60000);
                    const hrs = Math.floor(mins / 60);
                    const days = Math.floor(hrs / 24);
                    if (days > 0) return `${days}d ago`;
                    if (hrs > 0) return `${hrs}h ago`;
                    return `${mins}m ago`;
                  })();

                  return (
                    <div key={ticket.id} className="flex items-center gap-4 px-4 py-3 hover:bg-gray-50 transition-all">
                      {/* Severity indicator */}
                      <div className="flex-shrink-0">
                        <div
                          className="w-9 h-9 rounded-xl flex items-center justify-center"
                          style={{ backgroundColor: severityBg, border: `1.5px solid ${priorityBorderColor}` }}
                        >
                          <Icon name="ExclamationTriangleIcon" size={16} style={{ color: severityColor }} />
                        </div>
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="font-mono text-xs font-bold" style={{ color: '#EC4899' }}>{ticket.ticket_id}</span>
                          <span
                            className="text-xs font-semibold px-2 py-0.5 rounded-full border"
                            style={{ backgroundColor: severityBg, color: priorityTextColor, borderColor: priorityBorderColor }}
                          >
                            {(ticket.priority || 'low').toUpperCase()}
                          </span>
                          <span
                            className="text-xs font-semibold px-2 py-0.5 rounded-full border"
                            style={{ backgroundColor: sc.bg, color: sc.text, borderColor: sc.border }}
                          >
                            {ticket.status?.replace('_', ' ').toUpperCase()}
                          </span>
                        </div>
                        <p className="text-sm font-semibold text-gray-800 truncate">{ticket.title}</p>
                        <div className="flex items-center gap-3 mt-1 flex-wrap">
                          <span className="text-xs text-gray-400 flex items-center gap-1">
                            <Icon name="ClockIcon" size={11} />
                            {timeAgoStr}
                          </span>
                          {ticket.response_due && (
                            <span className={`text-xs flex items-center gap-1 font-medium ${isResponseBreached ? 'text-red-500' : 'text-gray-400'}`}>
                              {isResponseBreached && <Icon name="ExclamationCircleIcon" size={11} />}
                              Response: {new Date(ticket.response_due).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}
                              {isResponseBreached && ' ⚠'}
                            </span>
                          )}
                          {ticket.resolution_due && (
                            <span className={`text-xs flex items-center gap-1 font-medium ${isResolutionBreached ? 'text-red-500' : 'text-gray-400'}`}>
                              {isResolutionBreached && <Icon name="ExclamationCircleIcon" size={11} />}
                              Resolve by: {new Date(ticket.resolution_due).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}
                              {isResolutionBreached && ' ⚠'}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Action */}
                      <div className="flex-shrink-0">
                        <a
                          href={`/escalation-management-dashboard?ticket=${ticket.ticket_id}`}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white transition-all hover:opacity-90 whitespace-nowrap"
                          style={{ backgroundColor: '#E89A3C' }}
                        >
                          <Icon name="ArrowTopRightOnSquareIcon" size={13} />
                          View Ticket
                        </a>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

      </div>

      {/* ── Slide-out Drawer ───────────────────────────────────────────── */}
      {drawerItem && (
        <ClaimDrawer item={drawerItem} onClose={() => setDrawerItem(null)} />
      )}

      {/* ── Toast ─────────────────────────────────────────────────────── */}
      {toast && (
        <div
          className={`fixed bottom-6 right-6 z-[100] flex items-center gap-3 px-5 py-3.5 rounded-xl shadow-xl text-white text-sm font-semibold transition-all duration-300 ${toast.type === 'success' ? 'bg-green-500' : 'bg-red-500'}`}
        >
          <Icon name={toast.type === 'success' ? 'CheckCircleIcon' : 'XCircleIcon'} size={18} variant="solid" />
          {toast.message}
        </div>
      )}

      {/* Drawer animation style */}
      <style jsx global>{`
        @keyframes slide-in-right {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        }
      `}</style>
    </div>
  );
}
